import { useEffect, useState } from "react";
import TopBar from "./components/TopBar.jsx";
import Landing from "./components/Landing.jsx";
import SkillMap from "./components/SkillMap.jsx";
import Lesson from "./components/Lesson.jsx";
import ExerciseRunner from "./components/ExerciseRunner.jsx";
import { Results, FinalReport, Certificate } from "./components/Results.jsx";
import Login from "./components/Login.jsx";
import { useAuth } from "./context/AuthContext.jsx";
import { useUserStats } from "./hooks/useUserStats.js";
import { LESSON_CONTENT, EXERCISES } from "./data/curriculum.js";

const SCREENS = {
  LANDING: "landing",
  SKILL_MAP: "skill-map",
  LESSON: "lesson",
  EXERCISES: "exercises",
  RESULTS: "results",
  REPORT: "report",
  CERTIFICATE: "certificate",
};

export default function App() {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div style={{ minHeight: "100vh", display: "grid", placeItems: "center", color: "var(--ink-3)" }}>
        <div className="pulse" style={{ fontFamily: "var(--font-serif)", fontSize: 40 }}>✳</div>
      </div>
    );
  }

  if (!isAuthenticated) return <Login />;
  return <Shell />;
}

function Shell() {
  const [screen, setScreen] = useState(() => localStorage.getItem("lc_screen") || SCREENS.LANDING);
  useEffect(() => {
    localStorage.setItem("lc_screen", screen);
  }, [screen]);

  const [state, setState] = useUserStats();

  const [activeTrack, setActiveTrack] = useState(null);
  const [runResults, setRunResults] = useState([]);
  const [heartsLeft, setHeartsLeft] = useState(5);

  const startTrack = (track) => {
    setActiveTrack(track);
    setScreen(SCREENS.SKILL_MAP);
  };

  const startExercises = () => {
    setRunResults([]);
    setScreen(SCREENS.EXERCISES);
  };

  const finishExercises = ({ results, heartsLeft }) => {
    const gemsGained = results.reduce((s, r) => s + (r.gems || 0), 0);
    const xpGained = results.filter((r) => r.correct).length * 25;
    setState((s) => ({
      ...s,
      gems: s.gems + gemsGained,
      xp: s.xp + xpGained,
      hearts: heartsLeft,
    }));
    setRunResults(results);
    setHeartsLeft(heartsLeft);
    setScreen(SCREENS.RESULTS);
  };

  const showBackTo = {
    [SCREENS.SKILL_MAP]: { label: "Home", onClick: () => setScreen(SCREENS.LANDING) },
    [SCREENS.LESSON]: { label: "Skill map", onClick: () => setScreen(SCREENS.SKILL_MAP) },
    [SCREENS.REPORT]: { label: "Skill map", onClick: () => setScreen(SCREENS.SKILL_MAP) },
    [SCREENS.CERTIFICATE]: { label: "Home", onClick: () => setScreen(SCREENS.LANDING) },
  }[screen];

  const hideTopbar = screen === SCREENS.EXERCISES;

  return (
    <>
      {!hideTopbar && (
        <TopBar state={state} onHome={() => setScreen(SCREENS.LANDING)} showBackTo={showBackTo} />
      )}

      {screen === SCREENS.LANDING && (
        <Landing
          onStartTrack={startTrack}
          onCustomTopic={(sugg) =>
            startTrack({
              id: "custom",
              title: sugg.title,
              subtitle: sugg.subtitle,
              color: "terracotta",
              glyph: "✳",
            })
          }
        />
      )}

      {screen === SCREENS.SKILL_MAP && (
        <SkillMap
          track={activeTrack}
          state={state}
          onStartLesson={() => setScreen(SCREENS.LESSON)}
        />
      )}

      {screen === SCREENS.LESSON && <Lesson lesson={LESSON_CONTENT} onDone={startExercises} />}

      {screen === SCREENS.EXERCISES && (
        <ExerciseRunner
          exercises={EXERCISES}
          onComplete={finishExercises}
          onBack={() => setScreen(SCREENS.SKILL_MAP)}
          gamIntensity={2}
        />
      )}

      {screen === SCREENS.RESULTS && (
        <Results
          results={runResults}
          heartsLeft={heartsLeft}
          onSeeReport={() => setScreen(SCREENS.REPORT)}
        />
      )}

      {screen === SCREENS.REPORT && (
        <FinalReport
          state={state}
          results={
            runResults.length
              ? runResults
              : EXERCISES.map((e, i) => ({ id: e.id, correct: i !== 3, gems: [10, 15, 20, 5, 10][i] }))
          }
          onCertificate={() => setScreen(SCREENS.CERTIFICATE)}
        />
      )}

      {screen === SCREENS.CERTIFICATE && (
        <Certificate
          state={state}
          results={
            runResults.length
              ? runResults
              : EXERCISES.map((e, i) => ({ id: e.id, correct: i !== 3, gems: [10, 15, 20, 5, 10][i] }))
          }
          onBackHome={() => setScreen(SCREENS.LANDING)}
        />
      )}
    </>
  );
}
